import { useMemo, useCallback } from 'react';
import axios from 'axios';
import { useConfigure } from '../context/ConfigureContext';

export const useStockValidation = (connectionString, companyId, AppRegID) => {
    const { configureData } = useConfigure();

    // Check if "Show Alerts on Positive(More then Max. Level)Stock?" configure is enabled
    const isStockAlertEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('useStockValidation: configureData not yet loaded or empty');
            return false;
        }
        console.log('useStockValidation: checking configureData', configureData);
        const config = configureData.find(
            c => c.DisplayMessage === 'Show Alerts on Positive(More then Max. Level)Stock?'
        );
        console.log('useStockValidation: found config?', config, 'ApplyConfigure:', config?.ApplyConfigure);
        return config ? Boolean(config.ApplyConfigure) : false;
    }, [configureData]);

    // Validate stock level: MaxLevel - (OpStock + StockInQty - StockOutQty + currentQty) > 0
    const validateStock = useCallback(async (itemId, currentQty = 0, L_No = null) => {
        // If stock alert is not enabled, skip validation
        if (!isStockAlertEnabled) {
            return {
                isValid: true,
                shouldShowWarning: false,
                message: '',
                maxLevel: 0,
                currentTotalStock: 0,
                availableCapacity: 0,
            };
        }

        if (!companyId || !itemId) {
            return {
                isValid: true,
                shouldShowWarning: false,
                message: '',
                maxLevel: 0,
                currentTotalStock: 0,
                availableCapacity: 0,
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
                currentQty: currentQty,
            };

            // If L_No is provided (edit mode), pass it to exclude current bill from stock calculation
            if (L_No) {
                params.L_No = L_No;
            }

            const res = await axios.get(
                `${connectionString}/api/stockinout/validate-stock-level`,
                { params }
            );

            const { maxLevel, currentTotalStock, availableCapacity, exceedsLimit } = res.data || {};

            return {
                isValid: !exceedsLimit,
                shouldShowWarning: exceedsLimit,
                message: exceedsLimit
                    ? `Stock will exceed maximum level by ${Math.abs(availableCapacity).toFixed(2)} units. Do you want to save?`
                    : '',
                maxLevel: maxLevel || 0,
                currentTotalStock: currentTotalStock || 0,
                availableCapacity: availableCapacity || 0,
            };
        } catch (err) {
            console.error('Stock validation error:', err);
            return {
                isValid: true,
                shouldShowWarning: false,
                message: 'Unable to validate stock',
                maxLevel: 0,
                currentTotalStock: 0,
                availableCapacity: 0,
                error: err,
            };
        }
    }, [isStockAlertEnabled, connectionString, companyId]);

    // Check if "Show Alerts on Negative(Less then Min. Level)Stock?" configure is enabled
    const isMinLevelAlertEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty');
            return false;
        }
        console.log('📋 useStockValidation: ALL configureData:', configureData.map(c => ({ DisplayMessage: c.DisplayMessage, ApplyConfigure: c.ApplyConfigure })));

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Show Alerts on Negative(Less then Min. Level)Stock?' ||
                 c.DisplayMessage === 'Show Alerts on Negative Stock?'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Min level config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('negative') ||
                    c.DisplayMessage.toLowerCase().includes('min') ||
                    c.DisplayMessage.toLowerCase().includes('below')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Min level config not found. Available configs:');
            configureData.forEach(c => console.log(`   - "${c.DisplayMessage}"`));
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found min level config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Validate min stock level: MinLevel - (OpStock + StockInQty - StockOutQty - currentQty) < 0
    // Mirror formula of Max Level: MinLevel < (currentStock - currentQty)
    // Rearranged: (currentStock - currentQty) < MinLevel means stock goes BELOW minimum
    // Which is: (currentStock - currentQty) - MinLevel < 0
    const validateMinLevel = useCallback(async (itemId, currentQty = 0, L_No = null) => {
        console.log('🔍 validateMinLevel called with:', { itemId, currentQty, L_No, isMinLevelAlertEnabled });

        // If stock alert is not enabled, skip validation
        if (!isMinLevelAlertEnabled) {
            console.warn('❌ Min level alerts disabled in configuration');
            return {
                isValid: true,
                shouldShowWarning: false,
                message: '',
                minLevel: 0,
                currentStock: 0,
                stockAfterQty: 0,
            };
        }

        if (!companyId || !itemId) {
            console.warn('❌ Missing companyId or itemId:', { companyId, itemId });
            return {
                isValid: true,
                shouldShowWarning: false,
                message: '',
                minLevel: 0,
                currentStock: 0,
                stockAfterQty: 0,
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
                currentQty: currentQty,
                validationType: 'minLevel',
            };

            // If L_No is provided (edit mode), pass it to exclude current bill from stock calculation
            if (L_No) {
                params.L_No = L_No;
            }

            console.log('📤 Sending validation request with params:', params);

            const res = await axios.get(
                `${connectionString}/api/stockinout/validate-stock-level`,
                { params }
            );

            console.log('📥 Validation response:', res.data);

            const { minLevel, currentStock, stockAfterQty, minLevelResult, belowMinLimit, message } = res.data || {};

            console.log('✅ Parsed validation data:', { minLevel, currentStock, stockAfterQty, minLevelResult, belowMinLimit, message });

            return {
                isValid: !belowMinLimit,
                shouldShowWarning: belowMinLimit,
                message: message || '',
                minLevel: minLevel || 0,
                currentStock: currentStock || 0,
                stockAfterQty: stockAfterQty || 0,
                minLevelResult: minLevelResult || 0,
            };
        } catch (err) {
            console.error('❌ Min level validation error:', err);
            return {
                isValid: true,
                shouldShowWarning: false,
                message: 'Unable to validate stock',
                minLevel: 0,
                currentStock: 0,
                stockAfterQty: 0,
                error: err,
            };
        }
    }, [isMinLevelAlertEnabled, connectionString, companyId]);

    // Check if "Last Sale Rate?" configure is enabled
    const isLastSaleRateEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Last Sale Rate');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Last Sale Rate', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Last Sale Rate?' ||
                 c.DisplayMessage === 'Use Last Sale Rate'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Last Sale Rate config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && c.DisplayMessage.toLowerCase().includes('last') &&
                (c.DisplayMessage.toLowerCase().includes('sale') || c.DisplayMessage.toLowerCase().includes('rate'))
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Last Sale Rate config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Last Sale Rate config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Fetch last sale rate and discount for a product
    const getLastSaleRate = useCallback(async (itemId, partyId) => {
        console.log('🔍 getLastSaleRate called with itemId:', itemId, 'partyId:', partyId, 'isLastSaleRateEnabled:', isLastSaleRateEnabled);

        // If last sale rate is not enabled, skip fetching
        if (!isLastSaleRateEnabled) {
            console.warn('❌ Last sale rate lookup disabled in configuration');
            return {
                rate: null,
                disc: null,
                message: 'Last sale rate lookup is disabled',
            };
        }

        if (!companyId || !itemId || !partyId) {
            console.warn('❌ Missing companyId, itemId, or partyId:', { companyId, itemId, partyId });
            return {
                rate: null,
                disc: null,
                message: 'Missing company, item, or party information',
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
                partyId: partyId,
            };

            console.log('📤 Fetching last sale rate (party-wise) with params:', params);

            const res = await axios.get(
                `${connectionString}/api/stockinout/get-last-sale-rate`,
                { params }
            );

            console.log('📥 Last sale rate response:', res.data);

            const { rate, disc, message } = res.data || {};

            console.log('✅ Last sale rate data:', { rate, disc, message });

            return {
                rate: rate ? Number(rate) : null,
                disc: disc ? Number(disc) : null,
                message: message || 'Last sale rate fetched successfully',
            };
        } catch (err) {
            console.error('❌ Last sale rate fetch error:', err);
            return {
                rate: null,
                disc: null,
                message: 'Unable to fetch last sale rate',
                error: err,
            };
        }
    }, [isLastSaleRateEnabled, connectionString, companyId]);

    // Check if "Last Purchase Rate?" configure is enabled
    const isLastPurchaseRateEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Last Purchase Rate');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Last Purchase Rate', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Last Purchase Rate?' ||
                 c.DisplayMessage === 'Use Last Purchase Rate'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Last Purchase Rate config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && c.DisplayMessage.toLowerCase().includes('last') &&
                (c.DisplayMessage.toLowerCase().includes('purchase') || c.DisplayMessage.toLowerCase().includes('purc'))
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Last Purchase Rate config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Last Purchase Rate config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Fetch last purchase rate and discount for a product
    const getLastPurchaseRate = useCallback(async (itemId, partyId) => {
        console.log('🔍 getLastPurchaseRate called with itemId:', itemId, 'partyId:', partyId, 'isLastPurchaseRateEnabled:', isLastPurchaseRateEnabled);

        // If last purchase rate is not enabled, skip fetching
        if (!isLastPurchaseRateEnabled) {
            console.warn('❌ Last purchase rate lookup disabled in configuration');
            return {
                rate: null,
                disc: null,
                message: 'Last purchase rate lookup is disabled',
            };
        }

        if (!companyId || !itemId || !partyId) {
            console.warn('❌ Missing companyId, itemId, or partyId:', { companyId, itemId, partyId });
            return {
                rate: null,
                disc: null,
                message: 'Missing company, item, or party information',
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
                partyId: partyId,
            };

            console.log('📤 Fetching last purchase rate (party-wise) with params:', params);

            const res = await axios.get(
                `${connectionString}/api/stockinout/get-last-purchase-rate`,
                { params }
            );

            console.log('📥 Last purchase rate response:', res.data);

            const { rate, disc, message } = res.data || {};

            console.log('✅ Last purchase rate data:', { rate, disc, message });

            return {
                rate: rate ? Number(rate) : null,
                disc: disc ? Number(disc) : null,
                message: message || 'Last purchase rate fetched successfully',
            };
        } catch (err) {
            console.error('❌ Last purchase rate fetch error:', err);
            return {
                rate: null,
                disc: null,
                message: 'Unable to fetch last purchase rate',
                error: err,
            };
        }
    }, [isLastPurchaseRateEnabled, connectionString, companyId]);

    // Check if "Don't Select Negative / Zero Level Stock Item (For Sale Challan,Sale Invoice)?" configure is enabled
    const isNegativeZeroStockBlockEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Negative/Zero Stock Block');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Negative/Zero Stock Block', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Don\'t Select Negative / Zero Level Stock Item (For Sale Challan,Sale Invoice)?' ||
                 c.DisplayMessage === "Don't Select Negative / Zero Level Stock Item (For Sale Challan,Sale Invoice)?" ||
                 c.DisplayMessage === 'Dont Select Negative / Zero Level Stock Item (For Sale Challan,Sale Invoice)?' ||
                 c.DisplayMessage === 'Block Negative/Zero Stock for Sales'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Negative/Zero Stock Block config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    (c.DisplayMessage.toLowerCase().includes('negative') || c.DisplayMessage.toLowerCase().includes('zero')) &&
                    (c.DisplayMessage.toLowerCase().includes('stock') || c.DisplayMessage.toLowerCase().includes('sale'))
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Negative/Zero Stock Block config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Negative/Zero Stock Block config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Validate if product has negative or zero stock (block if negative/zero and configure is enabled)
    const validateNegativeZeroStock = useCallback(async (itemId) => {
        console.log('🔍 validateNegativeZeroStock called with itemId:', itemId, 'isNegativeZeroStockBlockEnabled:', isNegativeZeroStockBlockEnabled);

        // If negative/zero stock warning is not enabled, skip validation
        if (!isNegativeZeroStockBlockEnabled) {
            console.warn('❌ Negative/Zero stock warning disabled in configuration');
            return {
                isBlocked: false,
                shouldShowWarning: false,
                message: 'Negative/Zero stock check is disabled',
                currentStock: null,
            };
        }

        if (!companyId || !itemId) {
            console.warn('❌ Missing companyId or itemId:', { companyId, itemId });
            return {
                isBlocked: false,
                shouldShowWarning: false,
                message: 'Missing company or item information',
                currentStock: null,
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
            };

            console.log('📤 Fetching current stock with params:', params);

            const res = await axios.get(
                `${connectionString}/api/stockinout/get-current-stock`,
                { params }
            );

            console.log('📥 Current stock response:', res.data);

            const { currentStock, message } = res.data || {};

            console.log('✅ Current stock data:', { currentStock, message });

            const isStockNegativeOrZero = currentStock <= 0;

            return {
                isBlocked: isStockNegativeOrZero,
                shouldShowWarning: isStockNegativeOrZero,
                message: isStockNegativeOrZero
                    ? `Cannot add product: Current stock is ${currentStock <= 0 ? 'zero or negative' : 'low'}. Available stock: ${currentStock}. Product cannot be added.`
                    : 'Stock is available',
                currentStock: currentStock || 0,
            };
        } catch (err) {
            console.error('❌ Negative/Zero stock validation error:', err);
            return {
                isBlocked: false,
                shouldShowWarning: false,
                message: 'Unable to validate stock',
                currentStock: null,
                error: err,
            };
        }
    }, [isNegativeZeroStockBlockEnabled, connectionString, companyId]);

    // Get warning dialog config for Alert component
    const getWarningDialogConfig = useCallback((validation) => {
        if (!validation.shouldShowWarning) return null;

        return {
            title: 'Stock Warning',
            message: validation.message,
            buttons: [
                {
                    text: 'Cancel',
                    onPress: () => ({ action: 'cancel' }),
                    style: 'cancel',
                },
                {
                    text: 'Yes, Save Anyway',
                    onPress: () => ({ action: 'confirm' }),
                    style: 'destructive',
                },
            ],
        };
    }, []);

    // Check if "Show Online Stock In Sale?" configure is enabled
    const isShowOnlineStockEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Show Online Stock');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Show Online Stock In Sale', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Show Online Stock In Sale?' ||
                 c.DisplayMessage === 'Show Online Stock In Sale'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Show Online Stock In Sale config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('online') &&
                    c.DisplayMessage.toLowerCase().includes('stock') &&
                    c.DisplayMessage.toLowerCase().includes('sale')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Show Online Stock In Sale config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Show Online Stock In Sale config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Show Online Stock In Purchase?" configure is enabled
    const isShowOnlineStockPurchaseEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Show Online Stock In Purchase');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Show Online Stock In Purchase', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Show Online Stock In Purchase?' ||
                 c.DisplayMessage === 'Show Online Stock In Purchase'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Show Online Stock In Purchase config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('online') &&
                    c.DisplayMessage.toLowerCase().includes('stock') &&
                    c.DisplayMessage.toLowerCase().includes('purchase')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Show Online Stock In Purchase config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Show Online Stock In Purchase config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Apply MRP in Sale?" configure is enabled
    const isApplyMRPInSaleEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply MRP in Sale');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply MRP in Sale', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply MRP in Sale?'
        );

        if (!config) {
            console.warn('⚠️ useStockValidation: Apply MRP in Sale config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply MRP in Sale config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    const isApplyMRPInPurchaseEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply MRP in Purchase');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply MRP in Purchase', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply MRP in Purchase?'
        );

        if (!config) {
            console.warn('⚠️ useStockValidation: Apply MRP in Purchase config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply MRP in Purchase config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Apply TDS?" configure is enabled
    const isApplyTDSEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply TDS');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply TDS', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply TDS?'
        );

        if (!config) {
            console.warn('⚠️ useStockValidation: Apply TDS config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply TDS config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Calculate Sale Rate from Sale MRP using tax percentage
    const calculateSaleRateFromMRP = useCallback((saleMRP, taxPercent) => {
        if (!saleMRP || saleMRP <= 0) return 0;
        const tax = Number(taxPercent) || 0;
        const calculatedRate = Number(saleMRP) / (1 + tax / 100);
        return Number(calculatedRate.toFixed(2));
    }, []);

    // Calculate Sale MRP from Sale Rate using tax percentage (reverse calculation)
    const calculateSaleMRPFromRate = useCallback((saleRate, taxPercent) => {
        if (!saleRate || saleRate <= 0) return 0;
        const tax = Number(taxPercent) || 0;
        const calculatedMRP = Number(saleRate) * (1 + tax / 100);
        return Number(calculatedMRP.toFixed(2));
    }, []);

    // Calculate Purchase Rate from Purchase MRP using tax percentage
    const calculatePurchaseRateFromMRP = useCallback((purchaseMRP, taxPercent) => {
        if (!purchaseMRP || purchaseMRP <= 0) return 0;
        const tax = Number(taxPercent) || 0;
        const calculatedRate = Number(purchaseMRP) / (1 + tax / 100);
        return Number(calculatedRate.toFixed(2));
    }, []);

    // Calculate Purchase MRP from Purchase Rate using tax percentage
    const calculatePurchaseMRPFromRate = useCallback((purchaseRate, taxPercent) => {
        if (!purchaseRate || purchaseRate <= 0) return 0;
        const tax = Number(taxPercent) || 0;
        const calculatedMRP = Number(purchaseRate) * (1 + tax / 100);
        return Number(calculatedMRP.toFixed(2));
    }, []);

    // Fetch online stock details (closing stock, max level, min level)
    const getOnlineStockDetails = useCallback(async (itemId) => {
        console.log('🔍 getOnlineStockDetails called with itemId:', itemId, 'isShowOnlineStockEnabled:', isShowOnlineStockEnabled);

        // If show online stock is not enabled, skip fetching
        if (!isShowOnlineStockEnabled) {
            console.warn('❌ Show online stock disabled in configuration');
            return {
                closingStock: null,
                maxLevel: null,
                minLevel: null,
                message: 'Show online stock is disabled',
            };
        }

        if (!companyId || !itemId) {
            console.warn('❌ Missing companyId or itemId:', { companyId, itemId });
            return {
                closingStock: null,
                maxLevel: null,
                minLevel: null,
                message: 'Missing company or item information',
            };
        }

        try {
            const params = {
                compId: companyId,
                itemId: itemId,
            };

            console.log('📤 Fetching online stock details with params:', params);

            const res = await axios.get(
                `${connectionString}/api/stockinout/get-online-stock-details`,
                { params }
            );

            console.log('📥 Online stock details response:', res.data);

            const { closingStock, maxLevel, minLevel, message } = res.data || {};

            console.log('✅ Online stock details data:', { closingStock, maxLevel, minLevel, message });

            return {
                closingStock: closingStock !== null ? Number(closingStock) : null,
                maxLevel: maxLevel !== null ? Number(maxLevel) : null,
                minLevel: minLevel !== null ? Number(minLevel) : null,
                message: message || 'Online stock details fetched successfully',
            };
        } catch (err) {
            console.error('❌ Online stock details fetch error:', err);
            return {
                closingStock: null,
                maxLevel: null,
                minLevel: null,
                message: 'Unable to fetch online stock details',
                error: err,
            };
        }
    }, [isShowOnlineStockEnabled, connectionString, companyId]);

    // Check if "Don't Make Sale Invoice if Limit is over?" configure is enabled
    const isDontMakeSaleInvoiceIfLimitOverEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Don\'t Make Sale Invoice if Limit is over');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Don\'t Make Sale Invoice if Limit is over', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Don\'t Make Sale Invoice if Limit is over?' ||
                 c.DisplayMessage === "Don't Make Sale Invoice if Limit is over?" ||
                 c.DisplayMessage === 'Block Sale Invoice if Credit Limit Exceeded' ||
                 c.DisplayMessage === 'Dont Make Sale Invoice if Limit is over?'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Don\'t Make Sale Invoice if Limit is over config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    (c.DisplayMessage.toLowerCase().includes('limit') || c.DisplayMessage.toLowerCase().includes('credit')) &&
                    (c.DisplayMessage.toLowerCase().includes('sale') || c.DisplayMessage.toLowerCase().includes('invoice'))
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Don\'t Make Sale Invoice if Limit is over config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Don\'t Make Sale Invoice if Limit is over config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Validate credit limit: fetch outstanding amount, add current bill amount, and compare with credit limit
    const validateCreditLimit = useCallback(async (clientId, currentBillAmount = 0) => {
        console.log('🔍 validateCreditLimit called with clientId:', clientId, 'currentBillAmount:', currentBillAmount, 'isDontMakeSaleInvoiceIfLimitOverEnabled:', isDontMakeSaleInvoiceIfLimitOverEnabled);

        // If credit limit check is not enabled, skip validation
        if (!isDontMakeSaleInvoiceIfLimitOverEnabled) {
            console.warn('❌ Credit limit check disabled in configuration');
            return {
                isWithinLimit: true,
                shouldShowWarning: false,
                message: 'Credit limit check is disabled',
                outstandingAmount: 0,
                creditLimit: 0,
                availableLimit: 0,
                totalAmount: 0,
            };
        }

        if (!companyId || !clientId) {
            console.warn('❌ Missing companyId or clientId:', { companyId, clientId });
            return {
                isWithinLimit: true,
                shouldShowWarning: false,
                message: 'Missing company or client information',
                outstandingAmount: 0,
                creditLimit: 0,
                availableLimit: 0,
                totalAmount: 0,
            };
        }

        try {
            const params = {
                compId: companyId,
                clientId: clientId,
            };

            console.log('📤 Fetching credit limit validation data with params:', params);

            const res = await axios.get(
                `${connectionString}/api/validate-credit-limit`,
                { params }
            );

            console.log('📥 Credit limit validation response:', res.data);

            const { outstandingAmount = 0, creditLimit = 0, message } = res.data || {};

            console.log('✅ Credit limit data received:', { outstandingAmount, creditLimit, message });

            // If creditLimit is 0 or not set, skip validation (no limit configured)
            if (Number(creditLimit) <= 0) {
                console.log('ℹ️ Credit limit is 0 or not configured, skipping validation');
                return {
                    isWithinLimit: true,
                    shouldShowWarning: false,
                    message: 'No credit limit configured for this client',
                    outstandingAmount: Number(outstandingAmount) || 0,
                    creditLimit: Number(creditLimit) || 0,
                    availableLimit: 0,
                    totalAmount: 0,
                };
            }

            const billAmount = Number(currentBillAmount) || 0;
            const totalAmount = Number(outstandingAmount) + billAmount;

            console.log('✅ Credit limit validation:', { outstandingAmount, billAmount, totalAmount, creditLimit });

            const availableLimit = creditLimit - totalAmount;
            const exceedsLimit = totalAmount > creditLimit;

            return {
                isWithinLimit: !exceedsLimit,
                shouldShowWarning: exceedsLimit,
                message: exceedsLimit
                    ? `Total Amount (Outstanding: ${Number(outstandingAmount).toFixed(2)} + Current Bill: ${billAmount.toFixed(2)} = ${totalAmount.toFixed(2)}) exceeds Credit Limit (${Number(creditLimit).toFixed(2)}). Please adjust bill amount or clear outstanding invoices before proceeding.`
                    : `Available Credit Limit: ${availableLimit.toFixed(2)}`,
                outstandingAmount: Number(outstandingAmount) || 0,
                creditLimit: Number(creditLimit) || 0,
                availableLimit: availableLimit || 0,
                totalAmount: totalAmount || 0,
            };
        } catch (err) {
            console.error('❌ Credit limit validation error:', err);
            return {
                isWithinLimit: true,
                shouldShowWarning: false,
                message: 'Unable to validate credit limit',
                outstandingAmount: 0,
                creditLimit: 0,
                availableLimit: 0,
                totalAmount: 0,
                error: err,
            };
        }
    }, [isDontMakeSaleInvoiceIfLimitOverEnabled, connectionString, companyId]);

    // Check if "Apply in Amt = Pkg*Qty*Rate?" configure is enabled
    const isApplyPkgInAmtEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply in Amt = Pkg*Qty*Rate');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply in Amt = Pkg*Qty*Rate');
        console.log('📋 ALL configureData items:', configureData.map(c => ({ DisplayMessage: c.DisplayMessage, ApplyConfigure: c.ApplyConfigure })));

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply in Amt = Pkg*Qty*Rate?' ||
                 c.DisplayMessage === "Apply in Amt = Pkg*Qty*Rate" ||
                 c.DisplayMessage === 'Apply Amount = Package * Quantity * Rate' ||
                 c.DisplayMessage === 'Pkg*Qty*Rate calculation'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Apply in Amt = Pkg*Qty*Rate config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('pkg') &&
                    c.DisplayMessage.toLowerCase().includes('qty') &&
                    c.DisplayMessage.toLowerCase().includes('rate')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Apply in Amt = Pkg*Qty*Rate config not found at all');
            console.warn('❌ Available config DisplayMessages:', configureData.map(c => c.DisplayMessage));
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply in Amt = Pkg*Qty*Rate config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Enable Convesrion effect in Transaction?" configure is enabled (note: database typo "Convesrion")
    const isApplyRateOnConversionEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            return false;
        }

        // Try exact match first (with database typo)
        let config = configureData.find(
            c => c.DisplayMessage === 'Enable Convesrion effect in Transaction?' ||
                 c.DisplayMessage === 'Enable Convesrion effect in Transaction'
        );

        // If no exact match, try partial match
        if (!config) {
            config = configureData.find(c =>
                c.DisplayMessage && (
                    (c.DisplayMessage.toLowerCase().includes('conversion') || c.DisplayMessage.toLowerCase().includes('convesrion')) &&
                    c.DisplayMessage.toLowerCase().includes('transaction')
                )
            );
        }

        if (!config) {
            return false;
        }

        return config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
    }, [configureData]);

    // Check if "Show Item List Partywise in Sale?" configure is enabled
    const isShowItemListPartywiseInSaleEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Show Item List Partywise in Sale');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Show Item List Partywise in Sale', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Show Item List Partywise in Sale?' ||
                 c.DisplayMessage === 'Show Item List Partywise in Sale'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Show Item List Partywise in Sale config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('partywise') &&
                    c.DisplayMessage.toLowerCase().includes('sale')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Show Item List Partywise in Sale config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Show Item List Partywise in Sale config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Show Item List Partywise in Purchase?" configure is enabled
    const isShowItemListPartywiseInPurchaseEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Show Item List Partywise in Purchase');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Show Item List Partywise in Purchase', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Show Item List Partywise in Purchase?' ||
                 c.DisplayMessage === 'Show Item List Partywise in Purchase'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Show Item List Partywise in Purchase config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('partywise') &&
                    c.DisplayMessage.toLowerCase().includes('purchase')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Show Item List Partywise in Purchase config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Show Item List Partywise in Purchase config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Check if "Apply Partywise/Price-listwise Disc. in Sale?" configure is enabled
    const isApplyPartywisePriceListDiscInSaleEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply Partywise/Price-listwise Disc in Sale');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply Partywise/Price-listwise Disc in Sale', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply Partywise/Price-listwise Disc. in Sale?' ||
                 c.DisplayMessage === 'Apply Partywise/Price-listwise Disc. in Sale'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Apply Partywise/Price-listwise Disc in Sale config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('price-listwise') ||
                    (c.DisplayMessage.toLowerCase().includes('disc') &&
                        c.DisplayMessage.toLowerCase().includes('partywise') &&
                        c.DisplayMessage.toLowerCase().includes('sale'))
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Apply Partywise/Price-listwise Disc in Sale config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply Partywise/Price-listwise Disc in Sale config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Fetch the price-list rate (NewPrice) for an item from pricelistdetails, if it exists
    const getPriceListRate = useCallback(async (itemId) => {
        if (!isApplyPartywisePriceListDiscInSaleEnabled || !companyId || !itemId) {
            return { found: false, newPrice: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/priceList/getItemPrice`, {
                params: { itemId, companyId, AppRegID },
            });
            console.log('✅ getPriceListRate:', res.data);
            return res.data || { found: false, newPrice: null };
        } catch (err) {
            console.error('❌ getPriceListRate error:', err);
            return { found: false, newPrice: null };
        }
    }, [isApplyPartywisePriceListDiscInSaleEnabled, connectionString, companyId, AppRegID]);

    // Fetch the client's own Disc% from clientmaster
    const getClientDiscount = useCallback(async (clientId) => {
        if (!isApplyPartywisePriceListDiscInSaleEnabled || !companyId || !clientId) {
            return { disc: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/client/getDiscount`, {
                params: { clientId, companyId, AppRegID },
            });
            console.log('✅ getClientDiscount:', res.data);
            return res.data || { disc: null };
        } catch (err) {
            console.error('❌ getClientDiscount error:', err);
            return { disc: null };
        }
    }, [isApplyPartywisePriceListDiscInSaleEnabled, connectionString, companyId, AppRegID]);

    // Fetch the Disc% for this item's group (groupwisedisc) for the given party —
    // used by "Apply Partywise/Price-listwise Disc. in Sale?" in place of clientmaster's
    // Disc%. Matches productmaster.ItemGrpID against groupwisedisc.ItemGrpId for this
    // party; if no match, returns { found: false } so the caller shows 0.00 (no fallback).
    const getGroupWiseDisc = useCallback(async (itemId, partyId) => {
        if (!isApplyPartywisePriceListDiscInSaleEnabled || !companyId || !itemId || !partyId) {
            return { found: false, disc: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/groupwise/getItemDisc`, {
                params: { itemId, partyId, companyId, AppRegID },
            });
            console.log('✅ getGroupWiseDisc:', res.data);
            return res.data || { found: false, disc: null };
        } catch (err) {
            console.error('❌ getGroupWiseDisc error:', err);
            return { found: false, disc: null };
        }
    }, [isApplyPartywisePriceListDiscInSaleEnabled, connectionString, companyId, AppRegID]);

    // Check if "Apply Partywise/Price-listwise Disc. in Purchase?" configure is enabled
    const isApplyPartywisePriceListDiscInPurchaseEnabled = useMemo(() => {
        if (!configureData || configureData.length === 0) {
            console.warn('❌ useStockValidation: configureData not yet loaded or empty for Apply Partywise/Price-listwise Disc in Purchase');
            return false;
        }
        console.log('📋 useStockValidation: checking configureData for Apply Partywise/Price-listwise Disc in Purchase', configureData);

        // Try exact match first
        let config = configureData.find(
            c => c.DisplayMessage === 'Apply Partywise/Price-listwise Disc. in Purchase?' ||
                 c.DisplayMessage === 'Apply Partywise/Price-listwise Disc. in Purchase'
        );

        // If no exact match, try partial match
        if (!config) {
            console.warn('⚠️ useStockValidation: Apply Partywise/Price-listwise Disc in Purchase config not found by exact match. Searching for partial match...');
            config = configureData.find(c =>
                c.DisplayMessage && (
                    c.DisplayMessage.toLowerCase().includes('disc') &&
                    c.DisplayMessage.toLowerCase().includes('partywise') &&
                    c.DisplayMessage.toLowerCase().includes('purchase')
                )
            );
            if (config) {
                console.log('📍 useStockValidation: Partial match found:', config.DisplayMessage);
            }
        }

        if (!config) {
            console.warn('❌ useStockValidation: Apply Partywise/Price-listwise Disc in Purchase config not found');
            return false;
        }

        const isEnabled = config.ApplyConfigure === 1 || config.ApplyConfigure === '1' || config.ApplyConfigure === true;
        console.log('✅ useStockValidation: found Apply Partywise/Price-listwise Disc in Purchase config:', config.DisplayMessage, 'ApplyConfigure value:', config.ApplyConfigure, 'isEnabled:', isEnabled);
        return isEnabled;
    }, [configureData]);

    // Fetch the price-list rate (NewPrice) for an item from pricelistdetails, if it exists (Purchase side)
    const getPriceListRatePurchase = useCallback(async (itemId) => {
        if (!isApplyPartywisePriceListDiscInPurchaseEnabled || !companyId || !itemId) {
            return { found: false, newPrice: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/priceList/getItemPrice`, {
                params: { itemId, companyId, AppRegID },
            });
            console.log('✅ getPriceListRatePurchase:', res.data);
            return res.data || { found: false, newPrice: null };
        } catch (err) {
            console.error('❌ getPriceListRatePurchase error:', err);
            return { found: false, newPrice: null };
        }
    }, [isApplyPartywisePriceListDiscInPurchaseEnabled, connectionString, companyId, AppRegID]);

    // Fetch the client's own Disc% from clientmaster (Purchase side)
    const getClientDiscountPurchase = useCallback(async (clientId) => {
        if (!isApplyPartywisePriceListDiscInPurchaseEnabled || !companyId || !clientId) {
            return { disc: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/client/getDiscount`, {
                params: { clientId, companyId, AppRegID },
            });
            console.log('✅ getClientDiscountPurchase:', res.data);
            return res.data || { disc: null };
        } catch (err) {
            console.error('❌ getClientDiscountPurchase error:', err);
            return { disc: null };
        }
    }, [isApplyPartywisePriceListDiscInPurchaseEnabled, connectionString, companyId, AppRegID]);

    // Fetch the Disc% for this item's group (groupwisedisc) for the given party (Purchase
    // side) — used by "Apply Partywise/Price-listwise Disc. in Purchase?" in place of
    // clientmaster's Disc%. Matches productmaster.ItemGrpID against groupwisedisc.ItemGrpId
    // for this party; if no match, returns { found: false } so the caller shows 0.00 (no fallback).
    const getGroupWiseDiscPurchase = useCallback(async (itemId, partyId) => {
        if (!isApplyPartywisePriceListDiscInPurchaseEnabled || !companyId || !itemId || !partyId) {
            return { found: false, disc: null };
        }

        try {
            const res = await axios.get(`${connectionString}/api/groupwise/getItemDisc`, {
                params: { itemId, partyId, companyId, AppRegID },
            });
            console.log('✅ getGroupWiseDiscPurchase:', res.data);
            return res.data || { found: false, disc: null };
        } catch (err) {
            console.error('❌ getGroupWiseDiscPurchase error:', err);
            return { found: false, disc: null };
        }
    }, [isApplyPartywisePriceListDiscInPurchaseEnabled, connectionString, companyId, AppRegID]);

    // Fetch the list of ItemIDs that are set up as party-wise items for a given party (partywiserate table)
    const getPartywiseItemIds = useCallback(async (partyId) => {
        console.log('🔍 getPartywiseItemIds called with partyId:', partyId, 'isShowItemListPartywiseInSaleEnabled:', isShowItemListPartywiseInSaleEnabled);

        if (!isShowItemListPartywiseInSaleEnabled || !companyId || !partyId) {
            return [];
        }

        try {
            const res = await axios.get(
                `${connectionString}/api/partywise/getByParty/${partyId}`,
                { params: { companyId, AppRegID } }
            );

            const rows = Array.isArray(res.data) ? res.data : [];
            const itemIds = rows.map(r => r.ItemID);
            console.log('✅ getPartywiseItemIds: found', itemIds.length, 'party-wise items for partyId', partyId);
            return itemIds;
        } catch (err) {
            console.error('❌ getPartywiseItemIds error:', err);
            return [];
        }
    }, [isShowItemListPartywiseInSaleEnabled, connectionString, companyId, AppRegID]);

    // Fetch this item's SaleRate/Disc from partywiserate for the given party — used when
    // "Show Item List Partywise in Sale?" is enabled, so the item's rate/disc come strictly
    // from partywiserate with NO fallback to productmaster's saleRate/SaleDisc.
    const getPartywiseRate = useCallback(async (itemId, partyId) => {
        if (!isShowItemListPartywiseInSaleEnabled || !companyId || !itemId || !partyId) {
            return { found: false, saleRate: null, disc: null };
        }

        try {
            const res = await axios.get(
                `${connectionString}/api/partywise/getByParty/${partyId}`,
                { params: { companyId, AppRegID } }
            );

            const rows = Array.isArray(res.data) ? res.data : [];
            const row = rows.find(r => String(r.ItemID) === String(itemId));
            if (!row) {
                return { found: false, saleRate: null, disc: null };
            }

            return {
                found: true,
                saleRate: row.SaleRate !== null && row.SaleRate !== undefined ? Number(row.SaleRate) : null,
                disc: row.Disc !== null && row.Disc !== undefined ? Number(row.Disc) : null,
            };
        } catch (err) {
            console.error('❌ getPartywiseRate error:', err);
            return { found: false, saleRate: null, disc: null };
        }
    }, [isShowItemListPartywiseInSaleEnabled, connectionString, companyId, AppRegID]);

    // Fetch this item's PurchaseRate/Disc from partywiserate for the given party — used when
    // "Show Item List Partywise in Purchase?" is enabled, so the item's rate/disc come strictly
    // from partywiserate with NO fallback to productmaster's purchaseRate/PurcDisc.
    const getPartywiseRatePurchase = useCallback(async (itemId, partyId) => {
        if (!isShowItemListPartywiseInPurchaseEnabled || !companyId || !itemId || !partyId) {
            return { found: false, purchaseRate: null, disc: null };
        }

        try {
            const res = await axios.get(
                `${connectionString}/api/partywise/getByParty/${partyId}`,
                { params: { companyId, AppRegID } }
            );

            const rows = Array.isArray(res.data) ? res.data : [];
            const row = rows.find(r => String(r.ItemID) === String(itemId));
            if (!row) {
                return { found: false, purchaseRate: null, disc: null };
            }

            return {
                found: true,
                purchaseRate: row.PurchaseRate !== null && row.PurchaseRate !== undefined ? Number(row.PurchaseRate) : null,
                disc: row.Disc !== null && row.Disc !== undefined ? Number(row.Disc) : null,
            };
        } catch (err) {
            console.error('❌ getPartywiseRatePurchase error:', err);
            return { found: false, purchaseRate: null, disc: null };
        }
    }, [isShowItemListPartywiseInPurchaseEnabled, connectionString, companyId, AppRegID]);

    // Calculate amount based on configuration: if Pkg*Qty*Rate enabled, use that; otherwise use Qty*Rate
    const calculateAmount = useCallback((qty, rate, pkg = null, discount = 0) => {
        const quantity = Number(qty) || 0;
        const unitRate = Number(rate) || 0;
        const package_ = Number(pkg) || 1;  // Default to 1 if not provided or 0
        const disc = Number(discount) || 0;

        let gross = 0;
        if (isApplyPkgInAmtEnabled) {
            // Calculate as: Pkg * Qty * Rate
            gross = package_ * quantity * unitRate;
        } else {
            // Calculate as: Qty * Rate
            gross = quantity * unitRate;
        }

        // Apply discount
        const taxable = gross * (1 - disc / 100);
        return {
            gross: Number(gross.toFixed(2)),
            taxable: Number(taxable.toFixed(2)),
        };
    }, [isApplyPkgInAmtEnabled]);

    // "Enable Conversion effect in Transaction?" — when a line item's Unit is changed away from
    // the product's own default Unit, the user confirms a Conversion Qty (how
    // many of the newly-picked unit equal 1 of the product's default unit) via a
    // popup. This derives the billed Rate from the product's base Sale/Purchase
    // Rate divided by that Conversion Qty, so the line can be billed in a
    // convenient unit while everything still reconciles back to the base unit.
    //
    //   Rate (billed)   = baseRate / conversionQty
    //   Amount          = Rate * qty   ==  (qty / conversionQty) * baseRate
    //   StockQty (base) = qty / conversionQty
    //
    // Returns conversionQty/baseRate unchanged (rate = baseRate, stockQty = qty)
    // whenever the feature is off or no conversion is active on this row, so
    // callers can always store the returned fields directly with no extra branching.
    const calculateConversion = useCallback((baseRate, qty, conversionQty) => {
        const rate0 = Number(baseRate) || 0;
        const qty0 = Number(qty) || 0;
        const convQty = Number(conversionQty) || 0;

        const isConversionActive = isApplyRateOnConversionEnabled && convQty > 0;

        const rate = isConversionActive ? rate0 / convQty : rate0;
        const stockQty = isConversionActive ? qty0 / convQty : qty0;

        return {
            rate: Number(rate.toFixed(2)),
            stockQty: Number(stockQty.toFixed(2)),
            conversionQty: isConversionActive ? convQty : null,
            conversionRate: isConversionActive ? rate0 : null,
        };
    }, [isApplyRateOnConversionEnabled]);

    return {
        isStockAlertEnabled,
        validateStock,
        isMinLevelAlertEnabled,
        validateMinLevel,
        isLastSaleRateEnabled,
        getLastSaleRate,
        isLastPurchaseRateEnabled,
        getLastPurchaseRate,
        isNegativeZeroStockBlockEnabled,
        validateNegativeZeroStock,
        isShowOnlineStockEnabled,
        isShowOnlineStockPurchaseEnabled,
        getOnlineStockDetails,
        getWarningDialogConfig,
        isApplyMRPInSaleEnabled,
        isApplyMRPInPurchaseEnabled,
        isApplyTDSEnabled,
        calculateSaleRateFromMRP,
        calculateSaleMRPFromRate,
        calculatePurchaseRateFromMRP,
        calculatePurchaseMRPFromRate,
        isDontMakeSaleInvoiceIfLimitOverEnabled,
        validateCreditLimit,
        isApplyPkgInAmtEnabled,
        calculateAmount,
        isApplyRateOnConversionEnabled,
        calculateConversion,
        isShowItemListPartywiseInSaleEnabled,
        getPartywiseItemIds,
        getPartywiseRate,
        getPartywiseRatePurchase,
        isShowItemListPartywiseInPurchaseEnabled,
        isApplyPartywisePriceListDiscInSaleEnabled,
        getPriceListRate,
        getClientDiscount,
        getGroupWiseDisc,
        isApplyPartywisePriceListDiscInPurchaseEnabled,
        getPriceListRatePurchase,
        getClientDiscountPurchase,
        getGroupWiseDiscPurchase,
    };
};
